<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';

import KanbanBoard from 'dashboard/components-next/kanban/KanbanBoard.vue';
import KanbanBoardSelector from 'dashboard/components-next/kanban/KanbanBoardSelector.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const store = useStore();
const { t } = useI18n();

const boards = useMapGetter('kanban/boards/getBoards');
const currentBoard = useMapGetter('kanban/boards/getCurrentBoard');
const currentBoardId = useMapGetter('kanban/boards/getCurrentBoardId');
const boardsUiFlags = useMapGetter('kanban/boards/getUIFlags');

const isLoading = computed(() => boardsUiFlags.value.isFetching);
const hasBoards = computed(() => boards.value.length > 0);

const handleBoardChange = id => {
  store.dispatch('kanban/boards/setCurrentBoard', id);
};

onMounted(() => {
  store.dispatch('kanban/boards/fetch');
});
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-background">
    <header
      class="flex items-center gap-4 px-6 py-4 border-b border-n-weak bg-n-solid-1"
    >
      <h1 class="text-lg font-semibold text-n-slate-12">
        {{ t('KANBAN.HEADER.TITLE') }}
      </h1>
      <KanbanBoardSelector
        v-if="hasBoards"
        :boards="boards"
        :model-value="currentBoardId"
        @update:model-value="handleBoardChange"
      />
    </header>

    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <span class="text-sm text-n-slate-11">
        {{ t('KANBAN.STATE.LOADING') }}
      </span>
    </div>

    <div
      v-else-if="!hasBoards"
      class="flex flex-col items-center justify-center flex-1 gap-3 p-6"
    >
      <Icon icon="i-lucide-columns-3" class="size-10 text-n-slate-9" />
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('KANBAN.EMPTY.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11 max-w-md text-center">
        {{ t('KANBAN.EMPTY.DESCRIPTION') }}
      </p>
    </div>

    <KanbanBoard
      v-else-if="currentBoard"
      :board="currentBoard"
      class="flex-1"
    />
  </section>
</template>
